/**
 * Skill Hub 超管面纯前端 helper（PR-3）。
 * - base64 分块编码（20MiB 安全）
 * - metadata 严格组装（不发送 undefined / 空可选字段）
 * - 上游 `{ success, data }` 信封解包
 * - 生命周期动作可用性（与 store.publish / unpublish / rollback 对齐）
 */

import type {
  SkillHubAdminItemView,
  SkillHubItemKind,
  SkillHubItemMetadata,
  SkillHubItemStatus,
  SkillHubRevisionValidationStatus,
} from "./api-types.js";

/** 32 KiB 分块 —— 远小于 `String.fromCharCode(...arr)` 栈上限，可覆盖 20 MiB。 */
export const BASE64_CHUNK_SIZE = 0x8000;

/**
 * 把 Blob/ArrayBuffer 安全转成 base64 字符串。
 * 禁止一次性 `String.fromCharCode(...hugeUint8Array)`（会栈溢出）。
 */
export function uint8ArrayToBase64(bytes: Uint8Array, chunkSize = BASE64_CHUNK_SIZE): string {
  if (chunkSize <= 0) {
    throw new Error("chunkSize must be positive");
  }
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const end = Math.min(i + chunkSize, bytes.length);
    // 子区间转普通 number[] 再 apply —— 单块 ≤ 32K 不会爆栈。
    const slice = bytes.subarray(i, end);
    binary += String.fromCharCode.apply(null, Array.from(slice));
  }
  return btoa(binary);
}

/** 读 File/Blob → base64（分块路径，不解析 ZIP）。 */
export async function fileToBase64Chunked(file: Blob, chunkSize = BASE64_CHUNK_SIZE): Promise<string> {
  const buffer = await file.arrayBuffer();
  return uint8ArrayToBase64(new Uint8Array(buffer), chunkSize);
}

/** 逗号分隔 tags / useCases → 去空、去重（保序）。 */
export function splitCommaList(raw: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const part of raw.split(/[,，]/)) {
    const t = part.trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

export interface SkillHubMetadataFormInput {
  name: string;
  description: string;
  category: string;
  /** 逗号分隔 */
  tags: string;
  longDescription?: string;
  /** 逗号分隔 */
  useCases?: string;
  minDosiaVersion?: string;
  providedByPlugin?: string;
  /**
   * 文本数字：
   * - 空串 / 仅空白 → 忽略（不写字段）
   * - 合法非负整数 → 写入 number
   * - 非空但非法（abc / -1 / 1.2）→ throw，禁止 silent drop
   */
  featuredRank?: string;
}

/**
 * 严格组装 metadata：必填字段 trim；可选字段仅在非空时写入。
 * 不产出 undefined 键，便于 JSON.stringify 后零噪声 body。
 * @throws Error 当 featuredRank 非空但不是非负整数
 */
export function buildSkillHubMetadata(form: SkillHubMetadataFormInput): SkillHubItemMetadata {
  const meta: SkillHubItemMetadata = {
    name: form.name.trim(),
    description: form.description.trim(),
    category: form.category.trim(),
    tags: splitCommaList(form.tags),
  };
  const longDescription = form.longDescription?.trim();
  if (longDescription) meta.longDescription = longDescription;
  const useCases = form.useCases !== undefined ? splitCommaList(form.useCases) : [];
  if (useCases.length > 0) meta.useCases = useCases;
  const minDosiaVersion = form.minDosiaVersion?.trim();
  if (minDosiaVersion) meta.minDosiaVersion = minDosiaVersion;
  const providedByPlugin = form.providedByPlugin?.trim();
  if (providedByPlugin) meta.providedByPlugin = providedByPlugin;
  const rankRaw = form.featuredRank?.trim();
  if (rankRaw) {
    // 禁 Number("1.2")→1.2 后静默丢：非空必须是可解析的非负整数。
    if (!/^\d+$/.test(rankRaw)) {
      throw new Error(`featuredRank 须为非负整数，当前值「${rankRaw}」无效`);
    }
    const n = Number(rankRaw);
    if (!Number.isInteger(n) || n < 0) {
      throw new Error(`featuredRank 须为非负整数，当前值「${rankRaw}」无效`);
    }
    meta.featuredRank = n;
  }
  return meta;
}

/** 上游成功信封 → data；失败或缺 data 抛 Error（调用方 catch 转 UI）。 */
export function unwrapSkillHubData<T>(body: unknown): T {
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    throw new Error("Skill Hub 响应格式无效");
  }
  const rec = body as Record<string, unknown>;
  if (rec.success === false) {
    const err = rec.error;
    if (err && typeof err === "object" && !Array.isArray(err)) {
      const msg = (err as Record<string, unknown>).message;
      if (typeof msg === "string" && msg) throw new Error(msg);
    }
    throw new Error("Skill Hub 请求失败");
  }
  if (!("data" in rec)) {
    throw new Error("Skill Hub 响应缺少 data");
  }
  return rec.data as T;
}

export function findRevision(
  item: SkillHubAdminItemView,
  revisionId: string | null,
): SkillHubAdminItemView["revisions"][number] | null {
  if (!revisionId) return null;
  return item.revisions.find((r) => r.revision === revisionId) ?? null;
}

/** 列表「当前版本」：优先 current，其次 draft 的 manifest version。 */
export function itemVersionLabel(item: SkillHubAdminItemView): string {
  const rev =
    findRevision(item, item.currentRevision) ?? findRevision(item, item.draftRevision);
  if (!rev) return "—";
  return rev.version && rev.version !== "0.0.0" ? rev.version : rev.revision.slice(0, 14);
}

/** 草稿校验状态（无 draft 时 null）。 */
export function draftValidationStatus(
  item: SkillHubAdminItemView,
): SkillHubRevisionValidationStatus | null {
  const rev = findRevision(item, item.draftRevision);
  return rev?.validationStatus ?? null;
}

export type SkillHubLifecycleAction = "validate" | "publish" | "unpublish" | "rollback";

/**
 * 生命周期按钮可用性（与 apps/web/server/skill-hub/store 对齐，前端只做 UX 门）。
 * - validate：有 draftRevision（已上传包）
 * - publish：validated draft，或 unpublished 且有 current（可再发布）
 * - unpublish：status === published
 * - rollback：previousRevision 存在
 */
export function canLifecycle(
  item: SkillHubAdminItemView,
  action: SkillHubLifecycleAction,
): boolean {
  switch (action) {
    case "validate":
      return Boolean(item.draftRevision);
    case "publish": {
      const draft = findRevision(item, item.draftRevision);
      if (draft?.validationStatus === "validated") return true;
      if (item.status === "unpublished" && item.currentRevision && !item.draftRevision) {
        return true;
      }
      return false;
    }
    case "unpublish":
      return item.status === "published";
    case "rollback":
      return Boolean(item.previousRevision);
    default:
      return false;
  }
}

export function filterSkillHubItems(
  items: readonly SkillHubAdminItemView[],
  filters: {
    search: string;
    kind: "" | SkillHubItemKind;
    status: "" | SkillHubItemStatus;
  },
): SkillHubAdminItemView[] {
  const q = filters.search.trim().toLowerCase();
  return items.filter((item) => {
    if (filters.kind && item.kind !== filters.kind) return false;
    if (filters.status && item.status !== filters.status) return false;
    if (!q) return true;
    const hay = [
      item.metadata.name,
      item.slug,
      item.metadata.description,
      item.metadata.category,
      item.itemId,
    ]
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}

export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}
